import { create } from "zustand";
import firebase from "@services/firebase";
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
} from "firebase/auth";
import { loginAs as loginAsApi } from "@services/api/auth";

interface OriginalAdmin {
  token: string;
  uid: string;
}

interface AuthStore {
  user: User | null;
  loading: boolean;
  originalAdmin: OriginalAdmin | null;
  isImpersonating: boolean;
  initAuthListener: () => (() => void) | void;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginAs: (targetUid: string) => Promise<void>;
  returnToAdmin: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: true,
  originalAdmin: null,
  isImpersonating: false,

  initAuthListener: () => {
    const unsubscribe = onAuthStateChanged(firebase.auth, (u) => {
      set({ user: u, loading: false });
    });

    // Return unsubscribe function for cleanup
    return unsubscribe;
  },

  login: async (email, password, rememberMe = true) => {
    try {
      // Set persistence BEFORE signing in
      await setPersistence(
        firebase.auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence,
      );

      // Now sign in
      await signInWithEmailAndPassword(firebase.auth, email, password);

      // User will be set automatically by onAuthStateChanged listener
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },

  loginWithGoogle: async () => {
    try {
      await setPersistence(firebase.auth, browserLocalPersistence);
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebase.auth, provider);
      // onAuthStateChanged fires automatically; App.tsx calls fetchUserData
    } catch (error) {
      console.error("Google login error:", error);
      throw error;
    }
  },

  loginAs: async (targetUid: string) => {
    const currentUser = get().user;
    if (!currentUser) throw new Error("No user authenticated");

    // Get custom tokens for both target and admin (while still authenticated as admin)
    const targetCustomToken = await loginAsApi(targetUid);
    const adminCustomToken = await loginAsApi(currentUser.uid);

    // Save admin's custom token before switching
    set({
      originalAdmin: { token: adminCustomToken, uid: currentUser.uid },
    });

    // Sign in as the target user
    await signInWithCustomToken(firebase.auth, targetCustomToken);

    set({ isImpersonating: true });
    // onAuthStateChanged will fire and App.tsx will call fetchUserData
  },

  returnToAdmin: async () => {
    const { originalAdmin } = get();
    if (!originalAdmin) return;

    // Clear user so App.tsx useEffect re-triggers when admin uid comes back
    set({ user: null, originalAdmin: null, isImpersonating: false });

    // Sign back in as admin using the saved custom token
    await signInWithCustomToken(firebase.auth, originalAdmin.token);
    // onAuthStateChanged will fire and App.tsx will call fetchUserData
  },

  logout: async () => {
    try {
      const { isImpersonating } = get();

      if (isImpersonating) {
        // Return to admin instead of logging out
        await get().returnToAdmin();
        return;
      }

      await signOut(firebase.auth);
      set({ user: null, originalAdmin: null, isImpersonating: false });
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  },
}));
